import { useEffect, useState } from 'react';

/**
 * navigator.onLine plus explicit backend-reachability pings.
 *
 * navigator.onLine only knows about the network interface, so it happily
 * reports "online" when the backend container is down. `reportReachability` lets
 * the API layer correct it after a failed request.
 *
 * ## Three states, one source
 *
 * Two questions are answered from this one module, deliberately, so they can
 * never disagree:
 *
 * - {@link useOnlineStatus} — "can anything reach the server?", a boolean, and
 *   what the shell's banner and the session provider have always read.
 * - {@link connectionStatus} — the same observations split three ways, for
 *   {@link import('./useConnectionStatus').useConnectionStatus}. `reconnecting`
 *   is the state that used to be indistinguishable from `offline`: the
 *   interface is back, but nothing has heard from the backend yet.
 */

/** How long "the network is back" waits for the backend before calling it good. */
export const RECONNECT_PROBE_MS = 8_000;

export type ConnectionStatus = 'online' | 'offline' | 'reconnecting';

let reachable = true;
/** True between the `online` event and the first answer from the backend. */
let probing = false;
let probeTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<(value: boolean) => void>();

function browserOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function announce(): void {
  const value = browserOnline() && reachable;
  for (const listener of listeners) listener(value);
}

function endProbe(): void {
  if (probeTimer !== undefined) {
    clearTimeout(probeTimer);
    probeTimer = undefined;
  }
  if (!probing) return;
  probing = false;
  announce();
}

export function reportReachability(value: boolean): void {
  // An answer of any kind ends the probe: we have heard from the backend.
  if (value) endProbe();
  if (reachable === value) {
    if (!value) announce();
    return;
  }
  reachable = value;
  announce();
}

/**
 * The interface came back. Called from the `online` listener below; exported
 * so the reconnect window can be exercised without dispatching window events.
 */
export function noteInterfaceOnline(): void {
  if (probeTimer !== undefined) clearTimeout(probeTimer);
  probing = true;
  probeTimer = setTimeout(() => {
    probeTimer = undefined;
    // Nothing ever reported back — no request was in flight to report. Assume
    // the connection is good rather than leaving a spinner-ish state forever.
    if (probing) {
      probing = false;
      announce();
    }
  }, RECONNECT_PROBE_MS);
  announce();
}

export function connectionStatus(): ConnectionStatus {
  if (!browserOnline()) return 'offline';
  if (!reachable || probing) return 'reconnecting';
  return 'online';
}

/** Subscribe to any change in either observation. Returns the unsubscribe. */
export function subscribeConnection(listener: () => void): () => void {
  const wrapped = (): void => listener();
  const onOnline = (): void => {
    noteInterfaceOnline();
    listener();
  };
  const onOffline = (): void => {
    endProbe();
    listener();
  };
  listeners.add(wrapped);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  return () => {
    listeners.delete(wrapped);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

/**
 * The same answer, outside React.
 *
 * The composer needs it at the moment Send is pressed, and the Outbox flusher
 * needs it in a module with no component around it. Same two inputs as the
 * hook, so the banner and the decision to queue can never disagree.
 */
export function isOnline(): boolean {
  return (typeof navigator === 'undefined' || navigator.onLine) && reachable;
}

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => browserOnline() && reachable);

  useEffect(() => {
    const update = () => setOnline(browserOnline() && reachable);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    listeners.add(update);
    update();
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      listeners.delete(update);
    };
  }, []);

  return online;
}
